import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";

import {
  SUPPORT_SESSION_EVENT,
  endSupportSession,
  getActiveSupportSession,
  type ActiveSupportSession,
} from "./session";

/**
 * Persistent, non-dismissable banner shown while a Grey Matter support
 * session is active (decision #15). The tenant user being helped - and
 * anyone looking over their shoulder - always knows support is in the
 * account. Counts down the TTL and auto-ends at expiry.
 */
export const ImpersonationBanner = () => {
  const [session, setSession] = useState<ActiveSupportSession | null>(() =>
    getActiveSupportSession(),
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const sync = () => setSession(getActiveSupportSession());
    window.addEventListener(SUPPORT_SESSION_EVENT, sync);
    window.addEventListener("storage", sync);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      window.removeEventListener(SUPPORT_SESSION_EVENT, sync);
      window.removeEventListener("storage", sync);
      clearInterval(t);
    };
  }, []);

  if (!session) return null;

  const msLeft = new Date(session.expiresAt).getTime() - now;

  const end = () => {
    const ended = endSupportSession();
    window.location.hash = ended?.returnTo ?? "#/";
    window.location.reload();
  };

  if (msLeft <= 0) {
    // TTL expired - restore the operator.
    end();
    return null;
  }

  const mm = Math.floor(msLeft / 60000);
  const ss = Math.floor((msLeft % 60000) / 1000);

  return (
    <div className="sticky top-0 z-50 bg-[#b07d3f] text-white text-sm px-4 py-1.5 flex items-center gap-2 flex-wrap">
      <ShieldCheck className="w-4 h-4 shrink-0" />
      <span className="font-medium">Grey Matter support session</span>
      <span className="opacity-90">
        - viewing as {session.targetUserName}
        {session.mode === "read_only" ? " (read-only)" : " (write access)"} - ends in{" "}
        <span className="font-mono tabular-nums">
          {mm}:{String(ss).padStart(2, "0")}
        </span>
      </span>
      <button
        type="button"
        onClick={end}
        className="ml-auto rounded-md bg-white/15 hover:bg-white/25 px-2.5 py-0.5 text-xs font-medium transition-colors"
      >
        End session
      </button>
    </div>
  );
};
